import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SlideCard } from './SlideCard';
import { PAGE_CONFIG } from '../pageConfiguration/constants';
import { getColumnLetter } from '../pageConfiguration/constants';

interface HorizontalSlide {
  id: string;
  column: number;
  page?: any;
  isEmpty: boolean;
}

interface SlideGridProps {
  horizontalSlides: HorizontalSlide[];
  uploadingSlots: Set<string>;
  draggedSlide: HorizontalSlide | null;
  onDragStart: (e: React.DragEvent, slide: HorizontalSlide) => void;
  onDrop: (e: React.DragEvent, slide: HorizontalSlide) => void;
  onDragEnd: () => void;
  onImageSelect: (slideId: string, event: React.MouseEvent<HTMLElement>) => void;
  onDeleteSlide: (slide: HorizontalSlide) => void;
  onProcessImageFile: (file: File, slideId: string) => void;
  onAddColumn?: () => void;
  maxColumns?: number;
}

export const SlideGrid: React.FC<SlideGridProps> = ({
  horizontalSlides,
  uploadingSlots,
  draggedSlide,
  onDragStart,
  onDrop,
  onDragEnd,
  onImageSelect,
  onDeleteSlide,
  onProcessImageFile,
  onAddColumn,
  maxColumns = 1,
}) => {
  const canAddColumn = maxColumns < PAGE_CONFIG.MAX_COLUMNS && onAddColumn;

  // 空のスロット見た目の追加ボタン
  const EmptySlotButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        width: 168,
        height: 168, // 1:1の比率
        cursor: 'pointer',
        border: '1px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 0, height: '100%', position: 'relative', '&:last-child': { pb: 0 } }}>
        <Box
          sx={{
            height: '100%',
            position: 'relative',
            backgroundColor: '#f9f9f9',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)'
            }
          }}
        >
          {/* 中央のプラスアイコン */}
          <Box 
            sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 1,
              transition: 'opacity 0.2s',
            }}
          >
            <AddIcon sx={{ fontSize: '3rem', color: 'text.secondary' }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // 列名を取得
  const getVisibleColumns = () => {
    const columns = [];
    for (let i = 1; i <= maxColumns; i++) {
      columns.push(getColumnLetter(i));
    }
    return columns;
  };

  const visibleColumns = getVisibleColumns();

  return (
    <Box 
      sx={{ 
        width: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        p: 2,
        backgroundColor: 'white',
        overflowX: 'auto'
      }}
    >
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: `repeat(${visibleColumns.length}, 168px)${canAddColumn ? ' 168px' : ''}`,
          gap: 2,
          minWidth: 'fit-content'
        }}
      >
        {/* ヘッダー行 */}
        {visibleColumns.map(column => (
          <Box 
            key={column}
            textAlign="center" 
            sx={{ 
              py: 1,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body2" fontWeight="bold">{column}</Typography>
          </Box>
        ))}
        {canAddColumn && <Box></Box>}

        {/* スライドカード（列番号順に並べ替えて表示） */}
        {visibleColumns.map((column, index) => {
          const columnNumber = index + 1;
          const slide = horizontalSlides.find(s => s.column === columnNumber);
          if (!slide) return null;
          return (
            <SlideCard
              key={slide.id}
              slide={slide}
              isUploading={uploadingSlots.has(slide.id)}
              draggedSlide={draggedSlide}
              onDragStart={onDragStart}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              onImageSelect={onImageSelect}
              onDeleteSlide={onDeleteSlide}
              onProcessImageFile={onProcessImageFile}
            />
          );
        })}

        {/* 右側の列追加ボタン */}
        {canAddColumn && (
          <EmptySlotButton onClick={onAddColumn} />
        )}
      </Box>
    </Box>
  );
};

